"use client";

import {
  type SearchParamsLike,
  getAutoAgeGroupDecision,
  shouldDisableAgeSegmentation,
} from "@/lib/age-group/policy";
import { getAgeGroupFromStorage, setAgeGroupInStorage } from "@/lib/age-group/store";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";

/**
 * Headless auto age-group marker for Meta / pm_auto_age landings.
 * Mounted separately from any modal UI so paid-campaign visitors are still
 * marked grownup without the removed age-splitter popup.
 */
export function AutoAgeGroup() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ageSegmentationDisabled = shouldDisableAgeSegmentation(pathname);

  // Only depend on the specific params that affect our auto-age policy.
  // `utm_medium` is intentionally ignored (see `isMetaPaidUtm`).
  const utmSource = searchParams.get("utm_source");
  const pmAutoAge = searchParams.get("pm_auto_age");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (ageSegmentationDisabled) return;

    const autoAgeSearchParams: SearchParamsLike = {
      get(name) {
        switch (name) {
          case "utm_source":
            return utmSource;
          case "pm_auto_age":
            return pmAutoAge;
          default:
            return null;
        }
      },
    };

    const decision = getAutoAgeGroupDecision({ searchParams: autoAgeSearchParams });
    if (!decision) return;

    // Only set if no prior explicit choice exists.
    if (!getAgeGroupFromStorage()) {
      setAgeGroupInStorage(decision, { notify: true });
    }
  }, [ageSegmentationDisabled, utmSource, pmAutoAge]);

  return null;
}
